import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Search, Filter, Users, TrendingUp, AlertCircle, LogOut, Eye, Edit, Archive, RefreshCw, Download, FolderArchive } from "lucide-react";
import Layout from "../components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusBadgeClass = (status) => {
  const classes = {
    "Activo": "badge-activo",
    "Vencido": "badge-vencido",
    "Próximo a vencer": "badge-proximo",
    "Por comenzar": "badge-por-comenzar",
    "Renovado": "badge-renovado",
    "Archivado": "badge-archivado",
    "Terminado": "badge-archivado"
  };
  return `badge ${classes[status] || ""}`;
};

const formatCurrency = (amount, currency = "MXN") => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const formatDate = (dateString) => {
  // Parse date string without timezone conversion
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('es-MX');
};

export default function Dashboard({ user, onLogout }) {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [activeTab, setActiveTab] = useState("activos"); // "activos" or "archivados"
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [contractsRes, statsRes] = await Promise.all([
        axios.get(`${API}/contracts?estatus=${statusFilter}&search=${searchTerm}`, { headers }),
        axios.get(`${API}/dashboard/stats`, { headers })
      ]);

      setContracts(contractsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
      if (error.response?.status === 401) {
        onLogout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleSearch = () => {
    fetchData();
  };

  const handleArchive = async (contractId) => {
    if (!window.confirm("¿Estás seguro de archivar este contrato?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/contracts/${contractId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrato archivado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al archivar");
    }
  };

  const handleRenew = (contractId) => {
    navigate(`/contratos/${contractId}?action=renew`);
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/contracts/export/excel?estatus=${statusFilter}&search=${searchTerm}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'contratos.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel descargado exitosamente");
    } catch (error) {
      toast.error("Error al descargar el Excel");
      console.error(error);
    }
  };

  const handleDatabaseCleanup = async () => {
    if (!window.confirm("¿Estás seguro de que deseas actualizar la base de datos?\n\nEsto realizará:\n• Eliminar cargos, pagos y gastos huérfanos\n• Corregir contratos con cargos de más\n• Generar cargos faltantes en contratos renovados\n\n⚠️ Solo se eliminan cargos pendientes (no pagados).\nEsta acción no se puede deshacer.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      toast.info("Actualizando base de datos...", { duration: 2000 });
      
      const response = await axios.post(
        `${API}/database/cleanup`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const result = response.data;
      
      // Show detailed results
      let message = result.mensaje || "Actualización completada";
      if (result.detalles && result.detalles.length > 0) {
        message += "\n\nDetalles:\n" + result.detalles.join("\n");
      }
      
      toast.success(message, { duration: 8000 });
      
      // Refresh data after cleanup
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("No tienes permiso para realizar esta acción");
      } else {
        toast.error("Error al actualizar la base de datos");
      }
      console.error(error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedContracts = [...contracts].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle different types
      if (key === 'renta_mensual' || key === 'deposito_garantia') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (key === 'fecha_inicio' || key === 'fecha_termino') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setContracts(sortedContracts);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? 
      <span className="text-blue-600 ml-1">↑</span> : 
      <span className="text-blue-600 ml-1">↓</span>;
  };

  // Filter contracts by tab
  const activeContracts = contracts.filter(c => c.estatus !== "Archivado" && c.estatus !== "Terminado");
  const archivedContracts = contracts.filter(c => c.estatus === "Archivado" || c.estatus === "Terminado");
  const displayedContracts = activeTab === "activos" ? activeContracts : archivedContracts;

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contratos de Arrendamiento</h1>
            <p className="text-gray-500 mt-1">Gestiona todos tus contratos en un solo lugar</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={handleExportExcel} 
              variant="outline"
              data-testid="export-excel-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar Excel
            </Button>
            {user.rol === "maestro" && (
              <Button 
                onClick={handleDatabaseCleanup} 
                variant="outline"
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
                data-testid="cleanup-database-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar Base de Datos
              </Button>
            )}
            {(user.rol === "admin" || user.rol === "maestro") && (
              <Button onClick={() => navigate("/contratos/nuevo")} data-testid="nuevo-contrato-button">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Contrato
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card data-testid="stat-total">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Contratos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{stats.total_contratos}</div>
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-activos">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Contratos Activos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-green-600">{stats.contratos_activos}</div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-por-vencer">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Por Vencer</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-orange-600">{stats.contratos_por_vencer}</div>
                  <AlertCircle className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-renta-total">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Renta Mensual Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(stats.total_renta_mensual)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por número, propietario, inquilino o inmueble..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Button onClick={handleSearch} variant="outline" data-testid="search-button">
                  Buscar
                </Button>
              </div>

              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los estados</SelectItem>
                    <SelectItem value="Activo">Activo</SelectItem>
                    <SelectItem value="Por comenzar">Por comenzar</SelectItem>
                    <SelectItem value="Próximo a vencer">Próximo a vencer</SelectItem>
                    <SelectItem value="Vencido">Vencido</SelectItem>
                    <SelectItem value="Renovado">Renovado</SelectItem>
                  </SelectContent>
                </Select>

                {user.rol !== "propietario" && (
                  <Button
                    variant="outline"
                    onClick={() => navigate("/propietarios")}
                    data-testid="propietarios-button"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Propietarios
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contracts Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="activos" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Contratos ({activeContracts.length})
            </TabsTrigger>
            <TabsTrigger value="archivados" className="flex items-center gap-2">
              <FolderArchive className="w-4 h-4" />
              Archivados ({archivedContracts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {/* Contracts Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full" data-testid="contracts-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('numero_contrato')}
                    >
                      <div className="flex items-center">
                        Número
                        <SortIcon columnKey="numero_contrato" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('propietario_nombre')}
                    >
                      <div className="flex items-center">
                        Propietario
                        <SortIcon columnKey="propietario_nombre" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('inquilino_nombre')}
                    >
                      <div className="flex items-center">
                        Inquilino
                        <SortIcon columnKey="inquilino_nombre" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('inmueble_nombre')}
                    >
                      <div className="flex items-center">
                        Inmueble
                        <SortIcon columnKey="inmueble_nombre" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('renta_mensual')}
                    >
                      <div className="flex items-center">
                        Renta
                        <SortIcon columnKey="renta_mensual" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('fecha_termino')}
                    >
                      <div className="flex items-center">
                        Término
                        <SortIcon columnKey="fecha_termino" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('estatus')}
                    >
                      <div className="flex items-center">
                        Estado
                        <SortIcon columnKey="estatus" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedContracts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        {activeTab === "activos" 
                          ? "No se encontraron contratos activos" 
                          : "No hay contratos archivados"}
                      </td>
                    </tr>
                  ) : (
                    displayedContracts.map((contract) => (
                      <tr key={contract.id} className="hover:bg-gray-50" data-testid={`contract-row-${contract.numero_contrato}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {contract.numero_contrato}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contract.propietario_nombre}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contract.inquilino_nombre}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {contract.inmueble_nombre}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(contract.renta_mensual, contract.moneda)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(contract.fecha_termino)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadgeClass(contract.estatus)} data-testid={`status-${contract.numero_contrato}`}>
                            {contract.estatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/contratos/${contract.id}`)}
                              data-testid={`view-button-${contract.numero_contrato}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {(user.rol === "admin" || user.rol === "maestro") && contract.estatus !== "Archivado" && contract.estatus !== "Renovado" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/contratos/editar/${contract.id}`)}
                                data-testid={`edit-button-${contract.numero_contrato}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {(user.rol === "admin" || user.rol === "maestro") && (contract.estatus === "Vencido" || contract.estatus === "Activo" || contract.estatus === "Próximo a vencer") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRenew(contract.id)}
                                data-testid={`renew-button-${contract.numero_contrato}`}
                              >
                                <RefreshCw className="w-4 h-4 text-blue-600" />
                              </Button>
                            )}
                            {(user.rol === "admin" || user.rol === "maestro") && (contract.estatus === "Vencido" || contract.estatus === "Renovado") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchive(contract.id)}
                                data-testid={`archive-button-${contract.numero_contrato}`}
                              >
                                <Archive className="w-4 h-4 text-gray-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
